import React, { useId, useMemo, useState } from "react"

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CompletionIndicator,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
} from "@coval/ui"
import { Cable, Info } from "lucide-react"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"
import { MultiPhoneInboundVoiceFields } from "@/components/shared/forms/MultiPhoneInboundVoiceFields"
import { OutboundCallConfigFields } from "@/components/shared/forms/OutboundCallConfigFields"
import { RealtimeAdvancedSettingsCard } from "@/components/shared/forms/RealtimeAdvancedSettingsFields"
import {
  InboundVoiceFields,
  LiveKitAdvancedFields,
  LiveKitFields,
  PhoneNumberField,
  SMS_TOOLTIPS,
} from "@/components/shared/forms/SimulatorFormFields"
import { ReadOnlyFieldset } from "@/components/version-history/ReadOnlyRegion"
import { useMultiPhoneSupport } from "@/hooks/useMultiPhoneSupport"
import {
  AudioCodec,
  SimulatorType,
  WebSocketConnectionMode,
} from "@/types/api/SimulatorType"
import type { ChatAgentMetadata } from "@/types/backend-contracts"
import { LANGUAGE_TYPES } from "@/types/ModelConfigs/LanguageTypes"
import { formatSimulatorType } from "@/utils/formatting/format-simulator-type"
import { isSipAddress } from "@/utils/persona/phoneNumberUtils"
import { PIPECAT_TOOLTIPS } from "@/utils/simulation/simulatorFields"

import {
  createLiveKitContentTypeUpdate,
  createLiveKitSandboxIdUpdate,
  getCurrentContentType,
  getCurrentSandboxId,
  getCustomHeaders,
  getCustomPayload,
} from "../utils/livekitHelpers"
import { createAgentPreviewMetadataView } from "./agent-preview-metadata-view"
import type { AgentTestConnectionConfig } from "./AgentTestConnectionButton"
import {
  AGENT_TYPE_GROUPS,
  AGENT_TYPE_ICONS,
  AgentTypeModal,
  AgentTypeTrigger,
} from "./AgentTypeModal"
import {
  createPhoneNumberIndexUpdate,
  createSimulatorStringUpdate,
  createSttProviderUpdate,
} from "./simulator-metadata-semantics"
import {
  ChatWebSocketSimulatorConfig,
  EmailSimulatorConfig,
  TextSimulatorConfig,
  WebSocketSimulatorConfig,
} from "./simulators"
import { ChatWebSocketAdvancedFields } from "./simulators/ChatWebSocketSimulatorConfig"
import { SdkWebsocketSimulatorConfig } from "./simulators/SdkWebsocketSimulatorConfig"
import { SecretInput } from "./simulators/SecretInput"
import { TextSimulatorAdvancedSettings } from "./simulators/TextSimulatorAdvancedSettings"
import { WebSocketAdvancedFields } from "./simulators/websocket-config/AdvancedConfigSection"
import { withTwimlWebhookRuntimeDefaults } from "./simulators/websocket-config/presets"
import { SipAdvancedSettingsCard } from "./SipAdvancedSettingsCard"

interface AgentPreviewSectionProps {
  simulatorType: SimulatorType
  simulationMetadata: Record<string, unknown>
  onSimulatorTypeChange: (value: string) => void
  onMetadataUpdate: (metadataUpdate: Record<string, unknown>) => void
  renderRequiredKeys: () => React.ReactNode
  advancedSettings?: React.ReactNode
  testConnection?: AgentTestConnectionConfig | null
  sectioned?: boolean
  connectionComplete?: boolean
  isSavedAgent?: boolean
}

export function AgentPreviewSection({
  simulatorType,
  simulationMetadata,
  onSimulatorTypeChange,
  onMetadataUpdate,
  renderRequiredKeys,
  advancedSettings,
  testConnection,
  sectioned = false,
  connectionComplete = false,
  isSavedAgent = false,
}: AgentPreviewSectionProps) {
  const pipecatApiKeyId = useId()
  const pipecatAgentNameId = useId()
  const [typeModalOpen, setTypeModalOpen] = useState(false)
  const [sipModeSelected, setSipModeSelected] = useState(false)
  const { hasMultiPhoneSupport } = useMultiPhoneSupport()
  const metadataView = createAgentPreviewMetadataView(simulationMetadata)
  const showSipAdvanced =
    isSipAddress(metadataView.voice.phoneNumber) || sipModeSelected

  const handleSimulatorTypeChange = (value: string) => {
    if (value !== String(SimulatorType.VOICE)) {
      setSipModeSelected(false)
    }
    onSimulatorTypeChange(value)
  }

  const isChatType =
    simulatorType === SimulatorType.TEXT ||
    simulatorType === SimulatorType.CHAT_WEBSOCKET

  const selectDisplayType =
    simulatorType === SimulatorType.CHAT_WEBSOCKET
      ? SimulatorType.TEXT
      : simulatorType

  const currentTypeOption = useMemo(
    () =>
      AGENT_TYPE_GROUPS.flatMap((group) => group.items).find(
        (item) => item.type === selectDisplayType,
      ),
    [selectDisplayType],
  )
  const CurrentTypeIcon = currentTypeOption
    ? AGENT_TYPE_ICONS[currentTypeOption.type]
    : AGENT_TYPE_ICONS[selectDisplayType]
  const currentTypeLabel =
    currentTypeOption?.label ||
    (simulatorType ? formatSimulatorType(simulatorType) : "")

  const agentTypeSelector = (
    <AgentTypeTrigger
      icon={CurrentTypeIcon}
      label={currentTypeLabel}
      description={currentTypeOption?.description}
      onClick={() => setTypeModalOpen(true)}
    />
  )

  const configurationFields = (
    <div className="space-y-4">
      {isChatType && (
        <div className="space-y-2">
          <FieldLabel tooltip="Choose whether this chat agent receives HTTP requests or a WebSocket connection.">
            Protocol
          </FieldLabel>
          <Tabs value={simulatorType} onValueChange={handleSimulatorTypeChange}>
            <TabsList
              variant="pill"
              aria-label="Chat protocol"
              className="w-full"
            >
              <TabsTrigger
                value={SimulatorType.TEXT}
                variant="pill"
                className="flex-1"
              >
                HTTP
              </TabsTrigger>
              <TabsTrigger
                value={SimulatorType.CHAT_WEBSOCKET}
                variant="pill"
                className="flex-1"
              >
                WebSocket
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {simulatorType === SimulatorType.VOICE &&
        (hasMultiPhoneSupport ? (
          <MultiPhoneInboundVoiceFields
            hidePlaceholders
            hideSipAdvancedFields={sectioned}
            onConnectionModeChange={(mode) =>
              setSipModeSelected(mode === "sip")
            }
            phoneNumber={metadataView.voice.phoneNumber}
            onPhoneNumberChange={(value) =>
              onMetadataUpdate({
                phone_number: value,
              })
            }
            phoneNumberIndex={Number(metadataView.voice.phoneNumberIndex) || 1}
            onPhoneNumberIndexChange={(value) =>
              onMetadataUpdate(createPhoneNumberIndexUpdate(value))
            }
            widebandEnabled={metadataView.voice.audioCodec === AudioCodec.L16}
            onWidebandChange={(enabled) =>
              onMetadataUpdate({
                audio_codec: enabled ? AudioCodec.L16 : AudioCodec.PCMU,
              })
            }
            customSipHeaders={metadataView.voice.customSipHeaders}
            onCustomSipHeadersChange={(value) =>
              onMetadataUpdate({ custom_sip_headers: value })
            }
            mediaEncryption={metadataView.voice.mediaEncryption}
            onMediaEncryptionChange={(value) =>
              onMetadataUpdate({ media_encryption: value })
            }
          />
        ) : (
          <InboundVoiceFields
            hidePlaceholders
            hideSipAdvancedFields={sectioned}
            onConnectionModeChange={(mode) =>
              setSipModeSelected(mode === "sip")
            }
            phoneNumber={metadataView.voice.phoneNumber}
            onPhoneNumberChange={(value) =>
              onMetadataUpdate({
                phone_number: value,
              })
            }
            customSipHeaders={metadataView.voice.customSipHeaders}
            onCustomSipHeadersChange={(value) =>
              onMetadataUpdate({ custom_sip_headers: value })
            }
            mediaEncryption={metadataView.voice.mediaEncryption}
            onMediaEncryptionChange={(value) =>
              onMetadataUpdate({ media_encryption: value })
            }
          />
        ))}

      {simulatorType === SimulatorType.SMS && (
        <PhoneNumberField
          hidePlaceholder
          value={metadataView.voice.phoneNumber}
          onChange={(value) => onMetadataUpdate({ phone_number: value })}
          labelTooltip={SMS_TOOLTIPS.phoneNumber}
        />
      )}
      {simulatorType === SimulatorType.EMAIL && (
        <EmailSimulatorConfig
          simulationMetadata={simulationMetadata}
          onMetadataUpdate={onMetadataUpdate}
        />
      )}
      {simulatorType === SimulatorType.OUTBOUND_VOICE && (
        <OutboundCallConfigFields
          fieldGroup={sectioned ? "primary" : "all"}
          config={metadataView.outboundCall}
          onConfigChange={(value) => onMetadataUpdate({ ...value })}
        />
      )}
      {simulatorType === SimulatorType.PIPECAT && (
        <div className="space-y-4">
          <div className="space-y-2">
            <FieldLabel
              htmlFor={pipecatApiKeyId}
              tooltip={PIPECAT_TOOLTIPS.apiKey}
            >
              Pipecat Cloud API Key
            </FieldLabel>
            <SecretInput
              id={pipecatApiKeyId}
              revealLabel="Pipecat Cloud API key"
              value={metadataView.pipecat.apiKey}
              placeholder="Enter your Pipecat Cloud API key"
              onChange={(e) =>
                onMetadataUpdate({
                  pipecat_api_key: e.target.value,
                })
              }
              aria-label="Pipecat Cloud API Key"
            />
          </div>
          <div className="space-y-2">
            <FieldLabel
              htmlFor={pipecatAgentNameId}
              tooltip={PIPECAT_TOOLTIPS.agentName}
            >
              Pipecat Agent Name
            </FieldLabel>
            <Input
              id={pipecatAgentNameId}
              value={metadataView.pipecat.agentName}
              placeholder="Enter your Pipecat agent name"
              onChange={(e) =>
                onMetadataUpdate({
                  agent_name: e.target.value,
                })
              }
              aria-label="Pipecat Agent Name"
            />
          </div>
          {!sectioned && (
            <div className="space-y-2">
              <FieldLabel tooltip={PIPECAT_TOOLTIPS.customData}>
                Custom Data
              </FieldLabel>
              <JsonCodeEditor
                value={metadataView.pipecat.customData}
                onChange={(value) =>
                  onMetadataUpdate({
                    custom_data: value,
                  })
                }
                ariaLabel="Custom Data"
                hideGutters
                copyable
              />
            </div>
          )}
        </div>
      )}

      {simulatorType !== SimulatorType.VOICE &&
        simulatorType !== SimulatorType.SMS &&
        simulatorType !== SimulatorType.EMAIL &&
        simulatorType !== SimulatorType.NOOP &&
        simulatorType !== SimulatorType.TEXT &&
        simulatorType !== SimulatorType.CHAT_A2A &&
        simulatorType !== SimulatorType.PIPECAT &&
        simulatorType !== SimulatorType.STT &&
        simulatorType !== SimulatorType.WEBSOCKET &&
        simulatorType !== SimulatorType.SDK_WEBSOCKET &&
        simulatorType !== SimulatorType.CHAT_WEBSOCKET &&
        renderRequiredKeys()}

      {simulatorType === SimulatorType.LIVEKIT && (
        <LiveKitFields
          hidePlaceholders
          hideAdvancedSettings={sectioned}
          tokenEndpoint={metadataView.livekit.tokenEndpoint}
          contentType={getCurrentContentType(simulationMetadata)}
          livekitUrl={metadataView.livekit.livekitUrl}
          agentName={metadataView.livekit.agentName}
          dispatchModeSelection={metadataView.livekit.dispatchMode}
          sandboxId={getCurrentSandboxId(simulationMetadata)}
          customHeaders={getCustomHeaders(simulationMetadata)}
          customPayload={getCustomPayload(simulationMetadata)}
          onTokenEndpointChange={(value) =>
            onMetadataUpdate({
              generate_token_endpoint: value,
            })
          }
          onContentTypeChange={(value) =>
            onMetadataUpdate(
              createLiveKitContentTypeUpdate(simulationMetadata, value),
            )
          }
          onLivekitUrlChange={(value) =>
            onMetadataUpdate({
              livekit_url: value,
            })
          }
          onAgentNameChange={(value) =>
            onMetadataUpdate({
              livekit_agent_name: value,
            })
          }
          onSandboxIdChange={(value) =>
            onMetadataUpdate(
              createLiveKitSandboxIdUpdate(simulationMetadata, value),
            )
          }
          onDispatchModeSelectionChange={(mode) =>
            onMetadataUpdate({
              livekit_dispatch_mode: mode,
            })
          }
          onCustomHeadersChange={(value) =>
            onMetadataUpdate({
              generate_token_headers: value,
            })
          }
          onCustomPayloadChange={(value) =>
            onMetadataUpdate({
              token_request_payload: value,
            })
          }
        />
      )}

      {simulatorType === SimulatorType.STT && (
        <div className="space-y-4">
          <div className="space-y-2">
            <FieldLabel tooltip="The speech-to-text service that transcribes audio. Providers may differ in accuracy and available features.">
              Provider
            </FieldLabel>
            <Select
              value={metadataView.stt.provider}
              onValueChange={(value) => {
                onMetadataUpdate(createSttProviderUpdate(value))
              }}
            >
              <SelectTrigger aria-label="Provider">
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="deepgram">Deepgram</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <FieldLabel tooltip="The STT model to use with the selected provider, such as whisper-1 for OpenAI.">
              Model
            </FieldLabel>
            <Input
              value={metadataView.stt.model}
              placeholder="Enter the transcription model"
              onChange={(e) =>
                onMetadataUpdate(
                  createSimulatorStringUpdate("model", e.target.value),
                )
              }
              disabled={metadataView.stt.provider === "openai"}
              aria-label="Model"
            />
          </div>
          <div className="space-y-2">
            <FieldLabel tooltip="The primary language expected in the audio input. This helps optimize transcription accuracy.">
              Language
            </FieldLabel>
            <Select
              value={metadataView.stt.language}
              onValueChange={(value) => {
                onMetadataUpdate(createSimulatorStringUpdate("language", value))
              }}
            >
              <SelectTrigger aria-label="Language">
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGE_TYPES.map((language) => (
                  <SelectItem key={language.id} value={language.id}>
                    {language.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {simulatorType === SimulatorType.TEXT && (
        <>
          {renderRequiredKeys() || (
            <TextSimulatorConfig
              fieldGroup={sectioned ? "primary" : "all"}
              simulatorType={simulatorType}
              simulationMetadata={simulationMetadata}
              onMetadataUpdate={onMetadataUpdate}
              testConnection={testConnection}
              hideAdvancedSettings={sectioned}
            />
          )}
        </>
      )}

      {simulatorType === SimulatorType.CHAT_A2A && renderRequiredKeys()}

      {simulatorType === SimulatorType.WEBSOCKET &&
        (renderRequiredKeys() || (
          <WebSocketSimulatorConfig
            simulatorType={simulatorType}
            simulationMetadata={metadataView.websocket}
            onMetadataUpdate={onMetadataUpdate}
            testConnection={testConnection}
            hideAdvancedSettings={sectioned}
          />
        ))}

      {simulatorType === SimulatorType.SDK_WEBSOCKET && (
        <SdkWebsocketSimulatorConfig />
      )}

      {simulatorType === SimulatorType.CHAT_WEBSOCKET && (
        <>
          {renderRequiredKeys() || (
            <ChatWebSocketSimulatorConfig
              simulatorType={simulatorType}
              simulationMetadata={metadataView.chatWebsocket}
              onMetadataUpdate={onMetadataUpdate}
              testConnection={testConnection}
              hideAdvancedSettings={sectioned}
            />
          )}
        </>
      )}
    </div>
  )

  const completionMark = (complete: boolean, label: string) =>
    !isSavedAgent || !complete ? (
      <CompletionIndicator complete={complete} label={label} />
    ) : null

  return (
    <>
      {sectioned ? (
        <div className="space-y-3">
          <Card>
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <CardTitle className="flex items-center gap-2">
                    <Cable className="size-4 shrink-0" />
                    Connection
                  </CardTitle>
                  <Tooltip
                    content="Choose how Coval connects to and communicates with your agent."
                    className="max-w-xs whitespace-normal"
                  >
                    <button type="button" aria-label="Connection info">
                      <Info className="size-3.5 text-coval-text-muted" />
                    </button>
                  </Tooltip>
                </div>
                {completionMark(Boolean(currentTypeLabel), "Connection")}
              </div>
            </CardHeader>
            <CardContent className="px-6 pb-6 pt-0">
              <ReadOnlyFieldset>
                {currentTypeLabel ? (
                  agentTypeSelector
                ) : (
                  <div className="flex justify-center py-2">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setTypeModalOpen(true)}
                      data-testid="agent-type-select-button"
                    >
                      Select Connection
                    </Button>
                  </div>
                )}
              </ReadOnlyFieldset>
            </CardContent>
          </Card>
          {currentTypeLabel && (
            <Card>
              <CardHeader className="p-6 pb-3">
                <div className="flex items-center justify-between gap-4">
                  <CardTitle className="flex items-center gap-2">
                    {CurrentTypeIcon && <CurrentTypeIcon className="size-4" />}
                    {currentTypeLabel} Configuration
                  </CardTitle>
                  {completionMark(connectionComplete, "Agent configuration")}
                </div>
              </CardHeader>
              <CardContent className="px-6 pb-6 pt-0">
                <ReadOnlyFieldset>{configurationFields}</ReadOnlyFieldset>
              </CardContent>
            </Card>
          )}
          {advancedSettings}
          {simulatorType === SimulatorType.VOICE && showSipAdvanced && (
            <SipAdvancedSettingsCard
              voice={metadataView.voice}
              hasMultiPhoneSupport={hasMultiPhoneSupport}
              onMetadataUpdate={onMetadataUpdate}
            />
          )}
          {simulatorType === SimulatorType.PIPECAT && (
            <RealtimeAdvancedSettingsCard>
              <div className="space-y-2">
                <FieldLabel tooltip={PIPECAT_TOOLTIPS.customData}>
                  Custom Data
                </FieldLabel>
                <JsonCodeEditor
                  value={metadataView.pipecat.customData}
                  onChange={(value) => onMetadataUpdate({ custom_data: value })}
                  ariaLabel="Custom Data"
                  hideGutters
                  copyable
                />
              </div>
            </RealtimeAdvancedSettingsCard>
          )}
          {simulatorType === SimulatorType.OUTBOUND_VOICE && (
            <RealtimeAdvancedSettingsCard>
              <OutboundCallConfigFields
                fieldGroup="advanced"
                config={metadataView.outboundCall}
                onConfigChange={(value) => onMetadataUpdate({ ...value })}
              />
            </RealtimeAdvancedSettingsCard>
          )}
          {simulatorType === SimulatorType.LIVEKIT && (
            <RealtimeAdvancedSettingsCard>
              <LiveKitAdvancedFields
                contentType={getCurrentContentType(simulationMetadata)}
                onContentTypeChange={(value) =>
                  onMetadataUpdate(
                    createLiveKitContentTypeUpdate(simulationMetadata, value),
                  )
                }
                sandboxId={getCurrentSandboxId(simulationMetadata)}
                customHeaders={getCustomHeaders(simulationMetadata)}
                customPayload={getCustomPayload(simulationMetadata)}
                onSandboxIdChange={(value) =>
                  onMetadataUpdate(
                    createLiveKitSandboxIdUpdate(simulationMetadata, value),
                  )
                }
                onCustomHeadersChange={(value) =>
                  onMetadataUpdate({ generate_token_headers: value })
                }
                onCustomPayloadChange={(value) =>
                  onMetadataUpdate({ token_request_payload: value })
                }
                hidePlaceholders
              />
            </RealtimeAdvancedSettingsCard>
          )}
          {simulatorType === SimulatorType.TEXT && (
            <RealtimeAdvancedSettingsCard>
              <div className="space-y-6">
                <TextSimulatorConfig
                  fieldGroup="advanced"
                  simulatorType={simulatorType}
                  simulationMetadata={
                    simulationMetadata as Partial<ChatAgentMetadata>
                  }
                  onMetadataUpdate={onMetadataUpdate}
                  hideAdvancedSettings
                />
                <TextSimulatorAdvancedSettings
                  metadata={simulationMetadata as Partial<ChatAgentMetadata>}
                  onMetadataUpdate={onMetadataUpdate}
                  disabled={false}
                  standalone
                />
              </div>
            </RealtimeAdvancedSettingsCard>
          )}
          {simulatorType === SimulatorType.WEBSOCKET && (
            <RealtimeAdvancedSettingsCard>
              <WebSocketAdvancedFields
                metadata={
                  metadataView.websocket.connection_mode ===
                  WebSocketConnectionMode.TWIML_WEBHOOK
                    ? withTwimlWebhookRuntimeDefaults(metadataView.websocket)
                    : metadataView.websocket
                }
                onMetadataUpdate={onMetadataUpdate}
                isHttpFirst={
                  metadataView.websocket.connection_mode ===
                    WebSocketConnectionMode.HTTP_FIRST ||
                  metadataView.websocket.connection_mode ===
                    WebSocketConnectionMode.TWIML_WEBHOOK
                }
              />
            </RealtimeAdvancedSettingsCard>
          )}
          {simulatorType === SimulatorType.CHAT_WEBSOCKET && (
            <RealtimeAdvancedSettingsCard>
              <ChatWebSocketAdvancedFields
                includeBehavior
                metadata={metadataView.chatWebsocket}
                onMetadataUpdate={onMetadataUpdate}
              />
            </RealtimeAdvancedSettingsCard>
          )}
        </div>
      ) : (
        <ReadOnlyFieldset className="space-y-4">
          {agentTypeSelector}
          {configurationFields}
        </ReadOnlyFieldset>
      )}
      <AgentTypeModal
        isOpen={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        onSelect={handleSimulatorTypeChange}
        groups={AGENT_TYPE_GROUPS}
        currentValue={selectDisplayType}
      />
    </>
  )
}
